import "./CompanyUsers.css";
import DataTable from "./components/DataTable.jsx";
import { usePermissions } from "./PermissionContext";
import { createPortal } from "react-dom";

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import axios from "axios";
import { toast } from "react-toastify";

const API_URL = "/api";

function getHeaders() {
  const token = localStorage.getItem("company_token");

  return {
    headers: token
      ? { Authorization: `Bearer ${token}` }
      : {},
  };
}

function getErrorMessage(error, fallback) {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  return fallback || "No fue posible completar la operación.";
}

function CompanyUsers() {
  const { can, role } = usePermissions();

  const canManagePermissions =
    ["admin", "superadmin"].includes(role) &&
    can("users.permissions");

  const isAdmin = ["admin", "superadmin"].includes(role);

  const [users, setUsers] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "employee",
  });

  const [showEditModal, setShowEditModal] = useState(false);

  const [showPermissionsModal, setShowPermissionsModal] =
    useState(false);

  const [editingUser, setEditingUser] = useState({
    id: null,
    name: "",
    email: "",
    role: "employee",
    password: "",
  });

  const [permissionUser, setPermissionUser] = useState({
    id: null,
    name: "",
    role: "employee",
  });

  const [permissionItems, setPermissionItems] = useState([]);

  const [loadingPermissions, setLoadingPermissions] =
    useState(false);

  const [savingPermissions, setSavingPermissions] =
    useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/users/me/company-users`,
        getHeaders()
      );

      setUsers(response.data);
    } catch (error) {
      toast.error(
        getErrorMessage(error, "Error cargando usuarios.")
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    try {
      const storedCompany =
        localStorage.getItem("company_info");

      if (storedCompany) {
        setCompanyInfo(JSON.parse(storedCompany));
      }
    } catch {
      toast.error(
        "No fue posible leer la información de la empresa."
      );
    }

    fetchUsers();
  }, [fetchUsers]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      await axios.post(
        `${API_URL}/users/me/company-users`,
        formData,
        getHeaders()
      );

      toast.success("Usuario creado correctamente.");

      setFormData({
        name: "",
        email: "",
        password: "",
        role: "employee",
      });

      await fetchUsers();
    } catch (error) {
      toast.error(
        getErrorMessage(error, "Error al crear el usuario.")
      );
    }
  };

  const openEditModal = useCallback((user) => {
    setEditingUser({
      id: user.id,
      name: user.name || "",
      email: user.email || "",
      role: user.role || "employee",
      password: "",
    });

    setShowEditModal(true);
  }, []);

  const closeEditModal = () => {
    setShowEditModal(false);
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;

    setEditingUser((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const saveUser = async () => {
    try {
      const userData = {
        name: editingUser.name,
        email: editingUser.email,
        role: editingUser.role,
      };

      if (editingUser.password.trim()) {
        userData.password = editingUser.password;
      }

      await axios.put(
        `${API_URL}/users/${editingUser.id}`,
        userData,
        getHeaders()
      );

      toast.success("Usuario actualizado correctamente.");
      setShowEditModal(false);

      await fetchUsers();
    } catch (error) {
      toast.error(
        getErrorMessage(error, "Error actualizando usuario.")
      );
    }
  };

  const openPermissionsModal = async (user) => {
    if (!canManagePermissions) return;

    setPermissionUser({
      id: user.id,
      name: user.name || "",
      role: user.role || "employee",
    });

    setPermissionItems([]);
    setShowPermissionsModal(true);
    setLoadingPermissions(true);

    try {
      const response = await axios.get(
        `${API_URL}/permissions/users/${user.id}`,
        getHeaders()
      );

      if (!Array.isArray(response.data)) {
        throw new Error(
          "La respuesta de permisos no tiene el formato esperado."
        );
      }

      setPermissionItems(response.data);
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          "No fue posible cargar los permisos del usuario."
        )
      );

      setShowPermissionsModal(false);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const togglePermission = (permissionId) => {
    setPermissionItems((current) =>
      current.map((permission) => {
        if (permission.permission_id !== permissionId) {
          return permission;
        }

        return {
          ...permission,
          is_allowed: !permission.is_allowed,
          has_override: true,
        };
      })
    );
  };

  const resetPermissionToRole = (permissionId) => {
    setPermissionItems((current) =>
      current.map((permission) => {
        if (permission.permission_id !== permissionId) {
          return permission;
        }

        return {
          ...permission,
          is_allowed: permission.inherited_from_role,
          has_override: false,
        };
      })
    );
  };

  const savePermissions = async () => {
    if (
      !permissionUser.id ||
      !canManagePermissions ||
      loadingPermissions ||
      savingPermissions
    ) {
      return;
    }

    setSavingPermissions(true);

    try {
      const user = users.find(
        (item) =>
          Number(item.id) === Number(permissionUser.id)
      );

      if (!user) {
        toast.error("No fue posible encontrar el usuario.");
        return;
      }

      // Actualiza el rol solamente si cambió.
      if (user.role !== permissionUser.role) {
        await axios.put(
          `${API_URL}/users/${permissionUser.id}`,
          { role: permissionUser.role },
          getHeaders()
        );

        // Refleja el cambio aunque falle después el guardado
        // de los permisos individuales.
        setUsers((current) =>
          current.map((item) =>
            Number(item.id) === Number(permissionUser.id)
              ? { ...item, role: permissionUser.role }
              : item
          )
        );
      }

      const overrides = permissionItems
        .filter((permission) => permission.has_override)
        .map((permission) => ({
          permission_id: permission.permission_id,
          is_allowed: permission.is_allowed,
        }));

      await axios.put(
        `${API_URL}/permissions/users/${permissionUser.id}`,
        { permissions: overrides },
        getHeaders()
      );

      toast.success(
        "Rol y permisos actualizados correctamente."
      );

      setShowPermissionsModal(false);
      setPermissionItems([]);

      await fetchUsers();
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          "No fue posible completar la actualización de permisos."
        )
      );
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleToggleActive = useCallback(
    async (user) => {
      const newStatus = !user.is_active;
      const action = newStatus ? "activar" : "inactivar";

      if (
        !window.confirm(
          `¿Deseas ${action} a ${user.name}?`
        )
      ) {
        return;
      }

      try {
        await axios.put(
          `${API_URL}/users/${user.id}`,
          { is_active: newStatus },
          getHeaders()
        );

        toast.success(
          `Usuario ${
            newStatus ? "activado" : "inactivado"
          } correctamente.`
        );

        await fetchUsers();
      } catch (error) {
        toast.error(
          getErrorMessage(
            error,
            `No fue posible ${action} al usuario.`
          )
        );
      }
    },
    [fetchUsers]
  );

  const columns = useMemo(
    () => [
      {
        title: "Nombre",
        data: "name",
      },
      {
        title: "Correo",
        data: "email",
      },
      {
        title: "Rol",
        data: "role",
        render: (userRole) => {
          if (userRole === "superadmin") {
            return (
              '<span class="role-badge admin">' +
              "Superadministrador</span>"
            );
          }

          return userRole === "admin"
            ? '<span class="role-badge admin">Administrador</span>'
            : '<span class="role-badge employee">Empleado</span>';
        },
      },
      {
        title: "Estado",
        data: "is_active",
        render: (isActive) =>
          isActive
            ? '<span class="status active">Activo</span>'
            : '<span class="status inactive">Inactivo</span>',
      },
      {
        title: "Permisos",
        data: null,
        orderable: false,
        searchable: false,
        render: (_, __, user) => {
          if (!canManagePermissions) {
            return "—";
          }

          const userId = Number(user.id);

          if (!Number.isSafeInteger(userId)) {
            return "—";
          }

          return `
            <button
              type="button"
              class="company-user-permissions-button"
              data-permissions-user-id="${userId}"
            >
              Permisos
            </button>
          `;
        },
      },
    ],
    [canManagePermissions]
  );

  const handlePermissionsTableClick = (event) => {
    if (!canManagePermissions) return;

    const target = event.target;

    if (!(target instanceof Element)) return;

    const button = target.closest(
      "button[data-permissions-user-id]"
    );

    if (
      !button ||
      !event.currentTarget.contains(button)
    ) {
      return;
    }

    const userId = Number(
      button.dataset.permissionsUserId
    );

    const selectedUser = users.find(
      (user) => Number(user.id) === userId
    );

    if (selectedUser) {
      openPermissionsModal(selectedUser);
    }
  };

  const used = users.length;

  const unlimited = companyInfo?.max_users === null;

  const max = unlimited
    ? null
    : Number(companyInfo?.max_users || 0);

  const available = unlimited
    ? null
    : Math.max(max - used, 0);

  const limitReached =
    !unlimited && max > 0 && used >= max;

  if (loading) {
    return <h2>Cargando...</h2>;
  }

  return (
    <div className="company-users">
      <h1>Gestión de Usuarios</h1>

      <div className="stats-card">
        <h3>
          Empresa:{" "}
          {companyInfo?.company_name || "Sin información"}
        </h3>

        <p>
          Usuarios usados: <strong>{used}</strong>
          {" / "}
          <strong>
            {unlimited ? "Ilimitados" : max}
          </strong>
        </p>

        <p>
          Disponibles:{" "}
          <strong>
            {unlimited ? "Ilimitados" : available}
          </strong>
        </p>
      </div>

      {isAdmin && (
        <form
          className="user-form"
          onSubmit={handleSubmit}
        >
          <h2>Crear Usuario</h2>

          <input
            type="text"
            name="name"
            placeholder="Nombre"
            aria-label="Nombre"
            value={formData.name}
            onChange={handleChange}
            required
          />

          <input
            type="email"
            name="email"
            placeholder="Correo"
            aria-label="Correo"
            value={formData.email}
            onChange={handleChange}
            required
          />

          <input
            type="password"
            name="password"
            placeholder="Contraseña"
            aria-label="Contraseña"
            autoComplete="new-password"
            value={formData.password}
            onChange={handleChange}
            required
          />

          <select
            name="role"
            aria-label="Rol del usuario"
            value={formData.role}
            onChange={handleChange}
          >
            <option value="employee">Empleado</option>

            {canManagePermissions && (
              <option value="admin">
                Administrador
              </option>
            )}
          </select>

          <button
            type="submit"
            disabled={limitReached}
          >
            {limitReached
              ? "Límite alcanzado"
              : "Crear Usuario"}
          </button>
        </form>
      )}

      <div
        className="table-container"
        onClick={handlePermissionsTableClick}
      >
        <DataTable
          data={users}
          columns={columns}
          onEdit={
            isAdmin ? openEditModal : undefined
          }
          onToggleActive={
            isAdmin ? handleToggleActive : undefined
          }
        />
      </div>

      {showEditModal &&
        createPortal(
          <div
            className="company-users-modal-overlay"
            onMouseDown={closeEditModal}
          >
            <section
              className="company-users-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="edit-user-title"
              onMouseDown={(event) =>
                event.stopPropagation()
              }
            >
              <header className="company-users-modal-header">
                <h2 id="edit-user-title">
                  Editar usuario
                </h2>

                <button
                  type="button"
                  className="company-users-modal-close"
                  aria-label="Cerrar"
                  onClick={closeEditModal}
                >
                  ×
                </button>
              </header>

              <div className="company-users-modal-body">
                <label htmlFor="edit-user-name">
                  Nombre
                </label>

                <input
                  id="edit-user-name"
                  type="text"
                  name="name"
                  value={editingUser.name}
                  onChange={handleEditChange}
                  required
                />

                <label htmlFor="edit-user-email">
                  Correo
                </label>

                <input
                  id="edit-user-email"
                  type="email"
                  name="email"
                  value={editingUser.email}
                  onChange={handleEditChange}
                  required
                />

                <label htmlFor="edit-user-password">
                  Nueva contraseña
                </label>

                <input
                  id="edit-user-password"
                  type="password"
                  name="password"
                  autoComplete="new-password"
                  placeholder="Déjalo vacío para conservar la actual"
                  value={editingUser.password}
                  onChange={handleEditChange}
                />

                <label htmlFor="edit-user-role">
                  Rol
                </label>

                <select
                  id="edit-user-role"
                  name="role"
                  value={editingUser.role}
                  onChange={handleEditChange}
                  disabled={!canManagePermissions}
                >
                  <option value="employee">
                    Empleado
                  </option>

                  <option value="admin">
                    Administrador
                  </option>

                  {editingUser.role === "superadmin" && (
                    <option value="superadmin">
                      Superadministrador
                    </option>
                  )}
                </select>
              </div>

              <footer className="company-users-modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={closeEditModal}
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  className="btn-save"
                  onClick={saveUser}
                >
                  Guardar cambios
                </button>
              </footer>
            </section>
          </div>,
          document.body
        )}

      {showPermissionsModal &&
        createPortal(
          <div
            className="company-users-modal-overlay"
            onMouseDown={() => {
              if (!savingPermissions && !loadingPermissions) {
                setShowPermissionsModal(false);
              }
            }}
          >
            <section
              className="company-users-modal company-permissions-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="permissions-user-title"
              onMouseDown={(event) =>
                event.stopPropagation()
              }
            >
              <header className="company-users-modal-header">
                <div>
                  <h2 id="permissions-user-title">
                    Rol y permisos
                  </h2>

                  <p className="company-permissions-user-name">
                    {permissionUser.name}
                  </p>
                </div>

                <button
                  type="button"
                  className="company-users-modal-close"
                  aria-label="Cerrar"
                  disabled={
                    savingPermissions || loadingPermissions
                  }
                  onClick={() =>
                    setShowPermissionsModal(false)
                  }
                >
                  ×
                </button>
              </header>

              <div className="company-users-modal-body">
                <label htmlFor="permission-user-role">
                  Rol asignado
                </label>

                <select
                  id="permission-user-role"
                  value={permissionUser.role}
                  disabled={
                    loadingPermissions || savingPermissions
                  }
                  onChange={(event) =>
                    setPermissionUser((previous) => ({
                      ...previous,
                      role: event.target.value,
                    }))
                  }
                >
                  <option value="employee">
                    Empleado
                  </option>

                  <option value="admin">
                    Administrador
                  </option>

                  {permissionUser.role === "superadmin" && (
                    <option value="superadmin">
                      Superadministrador
                    </option>
                  )}
                </select>

                <div className="company-permissions-help">
                  <strong>Cómo funciona:</strong>{" "}
                  Los empleados heredan los permisos del rol
                  y pueden tener permisos individuales.
                  Los administradores tienen todos los
                  permisos empresariales.
                </div>

                {loadingPermissions ? (
                  <p className="company-permissions-loading">
                    Cargando permisos...
                  </p>
                ) : (
                  <div className="company-permissions-list">
                    {permissionItems.length === 0 && (
                      <p>
                        No hay permisos disponibles para mostrar.
                      </p>
                    )}

                    {permissionItems.map((permission) => {
                      const fullAccessRole = [
                        "admin",
                        "superadmin",
                      ].includes(permissionUser.role);

                      return (
                        <article
                          className="company-permission-item"
                          key={permission.permission_id}
                        >
                          <div className="company-permission-content">
                            <strong>
                              {permission.name}
                            </strong>

                            <span>
                              {permission.description}
                            </span>

                            <small>
                              Código: {permission.code}
                              {" · "}

                              {fullAccessRole
                                ? "Acceso completo por rol"
                                : permission.inherited_from_role
                                  ? "Incluido en el rol actual"
                                  : "No incluido en el rol actual"}
                            </small>
                          </div>

                          <div className="company-permission-actions">
                            <label className="company-permission-switch">
                              <input
                                type="checkbox"
                                aria-label={permission.name}
                                checked={
                                  fullAccessRole ||
                                  Boolean(permission.is_allowed)
                                }
                                disabled={
                                  savingPermissions ||
                                  fullAccessRole
                                }
                                onChange={() =>
                                  togglePermission(
                                    permission.permission_id
                                  )
                                }
                              />

                              <span className="company-permission-slider" />
                            </label>

                            {!fullAccessRole &&
                              permission.has_override && (
                                <button
                                  type="button"
                                  className="company-permission-reset"
                                  disabled={savingPermissions}
                                  onClick={() =>
                                    resetPermissionToRole(
                                      permission.permission_id
                                    )
                                  }
                                >
                                  Restablecer
                                </button>
                              )}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>

              <footer className="company-users-modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  disabled={
                    savingPermissions || loadingPermissions
                  }
                  onClick={() =>
                    setShowPermissionsModal(false)
                  }
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  className="btn-save"
                  disabled={
                    loadingPermissions || savingPermissions
                  }
                  onClick={savePermissions}
                >
                  {savingPermissions
                    ? "Guardando..."
                    : "Guardar permisos"}
                </button>
              </footer>
            </section>
          </div>,
          document.body
        )}
    </div>
  );
}

export default CompanyUsers;